import { Component, OnInit, ViewChild } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Guardian } from '../../../../interfaces/Guardian';
import { Observable, Subject } from 'rxjs';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import * as XLSX from 'xlsx';
import { PermissionType } from '../../../../components/employee/PermissionType.enum';
import { UserRole } from '../../../../components/rename/add-user-role/add-user-role.component';
import { Centre } from '../../../../interfaces/Centres';
import { CentreService } from '../../../../services/centre.service';
import { MatSelectChange } from '@angular/material/select';
import firebase from 'firebase/compat';
import { ChildData } from '../../../../interfaces/FullChild';
import { environment } from '../../../../../environments/environment';
import { of } from 'rxjs';

export interface GuardianReportData extends Guardian {
  childFirstName: string;
  childLastName: string;
  childCode: string;
  centreName: string;
  centreCode: string;
}

@Component({
  selector: 'app-guardian-report',
  templateUrl: './guardian-report.component.html',
  styleUrls: ['./guardian-report.component.scss']
})
export class GuardianReportComponent implements OnInit {
  fileName = 'Guardian_Report.xlsx';
  isLoading = false;

  displayedColumns: string[] = ['position','childCode' ,'childFirstName', 'childLastName', 'centreName', 'firstName', 'lastName', 'relation', 'occupation', 'contactNo', 'skills'];
  guardianData: GuardianReportData[] = [];
  dataSource: MatTableDataSource<GuardianReportData>;
  private _destroyed$ = new Subject();

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  permissions = JSON.parse(localStorage.getItem('permissions')!) as UserRole[];
  guardianReport = this.permissions.filter((x) => x.functionName == 'Child Report');

  centres$: Observable<Centre[]>;
  selectedCentreCode?: string | null = 'All';
  startDate?: Date | null;
  endDate?: Date | null;
  selectedOccupations: string[] = [];
  selectedSkills: string[] = [];
  selectedRelations: string[] = [];

  occupations$: Observable<string[]>;
  skills$: Observable<string[]>;
  relations$: Observable<string[]>;

  private fetchedGuardians: GuardianReportData[] = [];

  dynamicColumns = [
    { label: 'Child First Name', value: 'childFirstName' },
    { label: 'Child Last Name', value: 'childLastName' },
    { label: 'Child Code', value: 'childCode' },
    { label: 'Centre Name', value: 'centreName' },
    { label: 'Guardian First Name', value: 'firstName' },
    { label: 'Guardian Middle Name', value: 'middleName' },
    { label: 'Guardian Last Name', value: 'lastName' },
    { label: 'Relation', value: 'relation' },
    { label: 'Occupation', value: 'occupation' },
    { label: 'Contact Number', value: 'contactNo' },
    { label: 'Alternate Contact', value: 'alternateContactNo' },
    { label: 'Skills', value: 'skills' },
    { label: 'Annual Income', value: 'annualIncome' },
    { label: 'Aadhar Number', value: 'aadharNumber' },
    { label: 'Election ID', value: 'electionId' },
    { label: 'Ration Card', value: 'rationCard' },
    { label: 'Other Documents', value: 'otherDocuments' }
  ];

  constructor(
    private readonly mFirestore: AngularFirestore,
    private readonly centreService: CentreService
  ) {
    this.dataSource = new MatTableDataSource(this.guardianData);
    this.centres$ = this.centreService.getAllCentres();
    this.occupations$ = of([]);
    this.skills$ = of([]);
    this.relations$ = of([]);
  }

  ngOnInit(): void {
    this.loadInitialData();
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  private capitalizeWords(str: string): string {
    if (!str) return '';
    return str.replace(/\b\w/g, char => char.toUpperCase());
  }

  loadInitialData() {
    this.isLoading = true;
    this.mFirestore.collection<ChildData>('ALChild',ref => ref.orderBy('childCode').limitToLast(100)).get().toPromise().then(snapshot => {
   //need only last 100 entries initially to lessen the reads
      const guardians: GuardianReportData[] = [];
      if (snapshot) {
        snapshot.docs.forEach(doc => {
          const childData = doc.data() as ChildData;
          if (childData.guardians) {
            childData.guardians.forEach((guardian: Guardian) => {
              guardians.push({
                ...guardian,
                childFirstName: childData.firstName,
                childLastName: childData.lastName,
                childCode: childData.childCode,
                centreName: childData.centreName,
                centreCode: childData.centreCode,
              });
            });
          }
        });
      }
      this.fetchedGuardians = guardians;

      const cleanAndUnique = (items: (string | string[] | undefined)[]): string[] => {
        const cleaned = items
          .filter((item): item is string | string[] => !!item)
          .flatMap(item => Array.isArray(item) ? item : [item])
          .map(item => this.capitalizeWords(item.trim()))
          .filter(item => item);
        return [...new Set(cleaned)].sort();
      };


      this.occupations$ = of(cleanAndUnique(this.fetchedGuardians.map(g => g.occupation)));
      this.skills$ = of(cleanAndUnique(this.fetchedGuardians.map(g => g.skills)));
      this.relations$ = of(cleanAndUnique(this.fetchedGuardians.map(g => g.relation)));

      this.dynamicQuery();
      this.isLoading = false;
    });
  }

  dynamicQuery() {
    this.isLoading = true;
    let query: firebase.firestore.Query<ChildData> = this.mFirestore.collection<ChildData>('ALChild').ref;

    if (this.selectedCentreCode && this.selectedCentreCode !== 'All') {
      query = query.where('centreCode', '==', this.selectedCentreCode);
    }
    if (this.startDate) {
      query = query.where('dateOfAdmission', '>=', this.startDate);
    }
    if (this.endDate) {
      this.endDate.setHours(23, 59, 59, 999);
      query = query.where('dateOfAdmission', '<=', this.endDate);
    }

    query.get().then(snapshot => {
      const guardians: GuardianReportData[] = [];
      snapshot.docs.forEach(doc => {
        const childData = doc.data() as ChildData;
        if (childData.guardians) {
          childData.guardians.forEach((guardian: Guardian) => {
            guardians.push({
              ...guardian,
              childFirstName: childData.firstName,
              childLastName: childData.lastName,
              childCode: childData.childCode,
              centreName: childData.centreName,
              centreCode: childData.centreCode,
            });
          });
        }
      });
      this.fetchedGuardians = guardians;
      const cleanAndUnique = (items: (string | string[] | undefined)[]): string[] => {
        const cleaned = items
          .filter((item): item is string | string[] => !!item)
          .flatMap(item => Array.isArray(item) ? item : [item])
          .map(item => this.capitalizeWords(item.trim()))
          .filter(item => item);
        return [...new Set(cleaned)].sort();
      };

      this.occupations$ = of(cleanAndUnique(this.fetchedGuardians.map(g => g.occupation)));
      this.skills$ = of(cleanAndUnique(this.fetchedGuardians.map(g => g.skills)));
      this.relations$ = of(cleanAndUnique(this.fetchedGuardians.map(g => g.relation)));
      this.applyClientSideFilters();
      this.isLoading = false;
    });
  }

  applyClientSideFilters() {
    let filteredData = [...this.fetchedGuardians];

    if (this.selectedOccupations && this.selectedOccupations.length > 0) {
      filteredData = filteredData.filter(g => g.occupation && this.selectedOccupations.includes(this.capitalizeWords(g.occupation.trim())));
    }
    if (this.selectedSkills && this.selectedSkills.length > 0) {
      filteredData = filteredData.filter(g => g.skills && this.selectedSkills.includes(this.capitalizeWords(g.skills.trim())));
    }
    if (this.selectedRelations && this.selectedRelations.length > 0) {
      filteredData = filteredData.filter(g => g.relation && this.selectedRelations.includes(this.capitalizeWords(g.relation.trim())));
    }

    this.guardianData = filteredData;
    this.dataSource.data = this.guardianData;
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  setSelectedCentreCode($event: MatSelectChange) {
    this.selectedCentreCode = $event.value;
  }

  onEndDateChanged() {
    if (this.endDate) {
      this.endDate.setHours(23, 59, 0, 0);
    }
  }

  clearAll() {
    this.startDate = null;
    this.endDate = null;
    this.selectedCentreCode = 'All';
    this.selectedOccupations = [];
    this.selectedSkills = [];
    this.selectedRelations = [];
    this.dynamicQuery();
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  onColumnsSelected(event: any) {
    this.displayedColumns = ['position', ...event.value];
  }

  downloadReport() {
    const element = document.getElementById('guardian-table');
    const ws: XLSX.WorkSheet = XLSX.utils.table_to_sheet(element);
    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Guardian Report');
    XLSX.writeFile(wb, this.fileName);
  }

  ngOnDestroy() {
    this._destroyed$.next(null);
    this._destroyed$.complete();
  }
}
